"use client";
import { useState, useEffect } from "react";
import { useAppSelector } from "@/hooks/hooks";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { ChevronLeft, ChevronRight, Link2Icon } from "lucide-react";
import Slider from "react-slick";
import "../styles/carousel.css";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { insertBookingReference } from "@/services/resort.services";
import dynamic from "next/dynamic";

const InquiryDialog = dynamic(() => import("./InquiryDialog"), { ssr: false });

function NextArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
      aria-label="Next slide"
    >
      <ChevronRight className="w-5 h-5" />
    </button>
  );
}

function PrevArrow(props: any) {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white rounded-full shadow-lg items-center justify-center hover:bg-pink-600 hover:text-white transition cursor-pointer border border-gray-100"
      aria-label="Previous slide"
    >
      <ChevronLeft className="w-5 h-5" />
    </button>
  );
}

export default function DholaviraTentCity({ cmsData }: { cmsData?: any }) {
  const router = useRouter();
  const { homeDetails } = useAppSelector((state) => state.home);
  const resortList = (homeDetails?.ResortListofDholavira ?? []).slice(0, 2);

  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [selectedResort, setSelectedResort] = useState<any>(null);

  const handleInquiryOpen = (resort: any) => {
    setSelectedResort(resort);
    setInquiryOpen(true);
  };

  // Dynamic slidesCount — reads actual window.innerWidth
  const getSlidesToShow = () => {
    if (typeof window === "undefined") return 2;
    const w = window.innerWidth;
    if (w <= 640) return 1;
    return 2;
  };

  const [slidesCount, setSlidesCount] = useState(getSlidesToShow);

  useEffect(() => {
    const onResize = () => setSlidesCount(getSlidesToShow());
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleBookNow = (resort: any) => {
    insertBookingReference(0, window.location.href).catch(() => {});
    if (resort.BookingURL) {
      window.open(`${resort.BookingURL}`, "_blank");
    } else {
      handleInquiryOpen(resort);
    }
  };

  const displayResorts =
    resortList.length > 0
      ? resortList.map((res: any) => ({
          ID: res.ID,
          name: res.Title || "",
          image: res.MainImage || res.BannerImage || "/placeholder.png",
          url: res.URL || "",
          BookingURL: res.BookingURL || "",
          price: res.Price || 0,
          PricePer: res.PricePer,
        }))
      : [];

  if (displayResorts.length === 0) {
    return null;
  }

  const showArrows = slidesCount > 1 && displayResorts.length > 1;

  const sliderSettings = {
    dots: true,
    infinite: displayResorts.length > slidesCount,
    speed: 500,
    slidesToShow: slidesCount,
    slidesToScroll: 1,
    autoplay: displayResorts.length > slidesCount,
    autoplaySpeed: 5000,
    swipe: true,
    touchMove: true,
    arrows: showArrows,
    nextArrow: showArrows ? <NextArrow /> : undefined,
    prevArrow: showArrows ? <PrevArrow /> : undefined,
  };

  // Resort card — reused in both slider and single-item fallback
  const ResortCard = ({ resort }: { resort: any }) => (
    <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group flex flex-col h-full">
      {/* Image */}
      <div className="relative h-48 overflow-hidden flex-shrink-0">
        <Link href={`/resort/${resort.url}`} className="block w-full h-full">
          <ImageWithFallback
            src={resort.image}
            alt={resort.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        </Link>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-grow justify-between gap-4">
        <div>
          <h3 className="text-lg font-sans font-medium text-gray-800 group-hover:text-pink-600 transition leading-snug">
            <Link href={`/resort/${resort.url}`}>{resort.name}</Link>
          </h3>
          <div
            className={`mt-4 font-sans ${resort.price > 0 ? "" : "invisible"}`}
          >
            <span className="text-xs text-gray-400 uppercase tracking-wider block font-semibold">
              Starting from
            </span>
            <div className="flex items-baseline gap-1 mt-0.5">
              <span className="text-2xl font-bold text-pink-600">
                ₹{resort.price.toLocaleString()}
              </span>
              <span className="text-xs text-gray-500 font-normal">
                / {resort.PricePer ? resort.PricePer.replace(/^\/+/, "").trim() : "Per Person"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex gap-2.5 mt-auto">
          <button
            onClick={() => handleBookNow(resort)}
            className="flex-1 bg-gradient-to-r from-pink-500 to-pink-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition text-sm cursor-pointer font-sans font-medium"
          >
            Book Now
          </button>
          <button
            onClick={() => handleInquiryOpen(resort)}
            className="flex-1 text-center border-2 border-pink-500 text-pink-600 px-4 py-2.5 rounded-lg hover:bg-pink-50 transition text-sm cursor-pointer font-sans font-medium flex items-center justify-center animate-none"
          >
            Inquiry Now
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <section className="py-20 bg-gradient-to-b from-pink-50 to-white">
      <div className="max-w-7xl mx-auto px-4">
        {cmsData?.Template ? (
          <div
            dangerouslySetInnerHTML={{ __html: cmsData.Template }}
            className="text-center mb-4"
          />
        ) : (
          <div className="text-center mb-4">
            <h2 className="text-4xl md:text-5xl text-pink-600 mb-4 font-sans font-semibold">
              Book Your Stay in Dholavira Tent City
            </h2>
            <p className="text-gray-600 max-w-4xl mx-auto font-sans font-normal leading-relaxed">
              Experience the natural beauty of Dholavira without compromising
              your comfort with our tents and resorts in the Dholavira Tent
              City. Experience the perfect harmony of nature and comfort with
              us.
            </p>
          </div>
        )}

        {displayResorts.length > 1 ? (
          <div className="relative px-0 sm:px-8 mt-12 overflow-visible">
            <Slider {...sliderSettings}>
              {displayResorts.map((resort: any, index: number) => (
                <div key={index} className="px-1 sm:px-3 pb-4">
                  <ResortCard resort={resort} />
                </div>
              ))}
            </Slider>
          </div>
        ) : displayResorts.length === 1 ? (
          <div className="mt-12 max-w-sm mx-auto">
            <ResortCard resort={displayResorts[0]} />
          </div>
        ) : null}
      </div>

      <InquiryDialog
        isOpen={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        contextName={selectedResort?.name}
        contextURL={selectedResort?.url}
        inquiryType="Dholavira"
      />
    </section>
  );
}
